import { modelOptions, post, prop } from "@typegoose/typegoose";
import { z } from "zod";
import type { milliseconds } from "../../../timeConstants.js";
import { getZodSchemaFromClass, validate } from "../../../zod-decorator/index.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, type SchemaConstructorArgs } from "../../schemaBase.js";
import {
    SensorTypeReadingConfigFieldsWithDefaults,
    SensorTypeReadingConfigSchemaTypes,
} from "../../types/sensorTypeReadingConfig.js";
import { DtoValidatorTypes } from "../../validatableClass.js";

const SCHEMA_KEY = "SensorTypeReadingConfig" as const;

@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<SensorTypeReadingConfig>>(...addSchemaKeyHook(SCHEMA_KEY))
export class SensorTypeReadingConfig extends SchemaBase<
    SensorTypeReadingConfig,
    SensorTypeReadingConfigFieldsWithDefaults
> {
    static schemaKey = SCHEMA_KEY;
    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: z.number().optional() })
    @prop({ type: () => Number, required: false })
    public readingPeriod?: milliseconds;

    @validate({ zodValidator: z.number().optional() })
    @prop({ type: () => Number, required: false })
    public uplinkPeriod?: milliseconds;

    static getters = {
        // Example getter: returns the readingPeriod property of the instance.
        getReadingPeriod: (self: SensorTypeReadingConfig): milliseconds | undefined => self.readingPeriod,
    } as const satisfies Record<string, (self: SensorTypeReadingConfig) => any>;

    static utilities = {
        // Example utility with proper typing
        updateReadingPeriod: (self: SensorTypeReadingConfig, newPeriod: milliseconds): SensorTypeReadingConfig => ({
            ...self,
            readingPeriod: newPeriod,
        }),
    } as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<SensorTypeReadingConfigSchemaTypes>;

    constructor(
        properties?: SchemaConstructorArgs<SensorTypeReadingConfig, SensorTypeReadingConfigFieldsWithDefaults>,
    ) {
        super(properties);
        Object.assign(this, properties);
    }
}

const SensorTypeReadingConfigSchema = getZodSchemaFromClass(SensorTypeReadingConfig);
SensorTypeReadingConfig.dtoValidators =
    SensorTypeReadingConfigSchema as unknown as DtoValidatorTypes<SensorTypeReadingConfigSchemaTypes>;
